import { Icon, Page, Paper, Skeleton, Typography } from '@fox/atoms';
import {
	FlexColumnSmallSpaced,
	FlexRowSpaced,
	Padded,
	PaddedLargeSide,
	ResponsiveCSSGrid,
	SpaceAroundRow,
	SpaceBetweenRow
} from '@fox/layout';
import styled, { useTheme } from 'styled-components';

export type Field = {
	/** The title is displayed as part of the key/value pair. */
	title: string;
	/** The value is displayed as part of the key/value pair. */
	value: string | boolean | number;
	/** The category key is used to determine unique sections. The heading will be used as the section heading. */
	category: Category;
	/** The colourOverride can be used to override the colour of a stat/value in the overview section. */
	colourOverride?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
	/** Optional handler for when this element is clicked */
	onClick?: () => void;
};

type Category = { key: string; heading: string };

const PageWrapper = styled.div`
	max-width: 70rem;
	padding-top: 1rem;
	margin: auto;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const StatContainer = styled.div<{ onClick?: () => void }>`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	align-items: center;
	${p => p.onClick && 'cursor: pointer'}
`;

function OverviewHeader({ fields, actionButton, loading }: { fields: Field[]; actionButton: JSX.Element; loading?: boolean }) {
	const category = fields[0].category;
	return (
		<Paper elevation={3}>
			<PaddedLargeSide>
				<SpaceBetweenRow style={{ flexWrap: 'wrap' }}>
					<Typography variant="h4">{category.heading}</Typography>
					{loading ? null : actionButton}
				</SpaceBetweenRow>
				<Padded>
					<SpaceAroundRow style={{ justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem' }}>
						{fields.map(stat => (
							<Stat
								key={stat.title}
								title={stat.title}
								value={stat.value}
								colourOverride={stat.colourOverride}
								onClick={stat.onClick}
								loading={loading}
							/>
						))}
					</SpaceAroundRow>
				</Padded>
			</PaddedLargeSide>
		</Paper>
	);
}

function Stat({ title, value, colourOverride, loading, onClick }: Omit<Field, 'category'> & { loading?: boolean }) {
	const theme = useTheme();
	return (
		<StatContainer onClick={onClick}>
			{loading ? (
				<Skeleton height="2rem" width="4rem" />
			) : (
				<Typography
					align="center"
					fontSize={'clamp(1.5rem, 2rem, 5vw)'}
					color={colourOverride ?? theme.palette.primary.main}
					variant="h1"
					component="p"
				>
					{value}
				</Typography>
			)}

			<Typography align="center" variant="body2">
				{title}
			</Typography>
		</StatContainer>
	);
}

function DetailRow({ title, value, loading }: Omit<Field, 'category'> & { loading?: boolean }) {
	const theme = useTheme();
	return (
		<FlexRowSpaced>
			<Typography sx={{ flex: '30%', minWidth: '7rem' }} variant="body2" color={theme.palette.text.secondary}>
				{title}
			</Typography>
			{loading ? (
				<Skeleton style={{ flex: '70%' }} height="2rem" width="5rem" />
			) : typeof value === 'boolean' ? (
				<div style={{ flex: '70%', width: 'max-content' }}>
					{value ? <Icon iconName="CheckCircle" color="primary" /> : <Icon iconName="Clear" />}
				</div>
			) : (
				<Typography sx={{ flex: '70%', wordBreak: 'break-word', width: 'max-content' }} variant="body1">
					{value}
				</Typography>
			)}
		</FlexRowSpaced>
	);
}

function Section({ fields, loading }: { fields: Field[]; loading?: boolean }) {
	const category = fields[0].category;
	return (
		<Paper elevation={3}>
			<PaddedLargeSide>
				<FlexColumnSmallSpaced>
					<Typography variant="h4">{category.heading}</Typography>
					{fields.map(field => (
						<DetailRow key={field.title} title={field.title} value={field.value} loading={loading} />
					))}
				</FlexColumnSmallSpaced>
			</PaddedLargeSide>
		</Paper>
	);
}

/**
 * This is an import overview page generator. It will generate a responsive layout for all required sections automatically based on the categories in your fields array.
 * @param {Field[]} fields - This will be used to generate the page sections.
 * @param {JSX.Element} actionButton - An action button to display at the top right of the Overview section
 * @returns {JSX.Element} The overview page with automatically generated sections
 */
export const OverviewPage = ({
	fields,
	actionButton,
	dataLoading
}: {
	fields: Field[];
	actionButton: JSX.Element;
	dataLoading?: boolean;
}) => {
	const overviewFields = fields.filter(f => f.category.key === 'Overview');
	const otherFields = fields.filter(f => f.category.key !== 'Overview');
	const uniqueSections = Array.from(new Set(otherFields.map(f => f.category.key)));
	return (
		<Page>
			<PageWrapper>
				{overviewFields.length > 0 ? (
					<OverviewHeader fields={overviewFields} actionButton={actionButton} loading={dataLoading} />
				) : null}
				<ResponsiveCSSGrid
					itemWidth="25rem"
					gap="1rem"
					fullWidth
					style={{
						gridAutoFlow: 'row'
					}}
				>
					{uniqueSections.map(s => {
						const fieldGroup = otherFields.filter(f => f.category.key === s);
						return <Section key={s} fields={fieldGroup} loading={dataLoading} />;
					})}
				</ResponsiveCSSGrid>
			</PageWrapper>
		</Page>
	);
};
